import {
  ACCESS_TOKEN_STORAGE_KEY,
  CURRENT_PROJECT_UUID_STORAGE_KEY,
  SAML_ORGANIZATION_UUID_STORAGE_KEY,
} from "@frontend-shared/constants";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type TFriendlyFileTypeKey =
  | "image"
  | "video"
  | "audio"
  | "csv"
  | "html"
  | "text"
  | "pdf"
  | "json"
  | "zip"
  | "word"
  | "spreadsheet"
  | "presentation"
  | "google_doc"
  | "google_sheet"
  | "google_slides"
  | "file";

// NOTE: Returns an i18n key under the `file_type.*` block of the common
// namespace; callers translate it via t(`file_type.${key}`, { ns: "common" }).
export const getFriendlyFileType = (mimeType: string): TFriendlyFileTypeKey => {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("text/")) {
    if (mimeType === "text/csv") return "csv";
    if (mimeType === "text/html") return "html";
    return "text";
  }
  switch (mimeType) {
    case "application/pdf":
      return "pdf";
    case "application/json":
      return "json";
    case "application/zip":
      return "zip";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "word";
    case "application/msword":
      return "word";
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "spreadsheet";
    case "application/vnd.ms-excel":
      return "spreadsheet";
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return "presentation";
    case "application/vnd.ms-powerpoint":
      return "presentation";
    case "application/vnd.google-apps.document":
      return "google_doc";
    case "application/vnd.google-apps.spreadsheet":
      return "google_sheet";
    case "application/vnd.google-apps.presentation":
      return "google_slides";
    default:
      return "file";
  }
};

export interface INumberOnlyChangeHandlerConfig {
  min?: number;
  max?: number;
  allowNegative?: boolean;
}

export const isStringNumberValid = (
  value: string,
  config?: INumberOnlyChangeHandlerConfig,
) => {
  if (value === "") {
    return true;
  }

  if (value === "-" && config?.allowNegative) {
    return true;
  }

  const convertedValue = Number(value);

  if (Number.isNaN(convertedValue)) {
    return false;
  }

  if (config?.min && convertedValue < config.min) {
    return false;
  }

  if (config?.max && convertedValue > config.max) {
    return false;
  }

  if (!config?.allowNegative && convertedValue < 0) {
    return false;
  }

  return true;
};

export const encryptEmail = (email: string): string => {
  const [local, domain] = email.split("@");
  const numAsterisks = local.length > 2 ? Math.floor(local.length / 2) : 1;
  const encryptedLocal =
    local.slice(0, local.length - numAsterisks) + "*".repeat(numAsterisks);
  return `${encryptedLocal}@${domain}`;
};

export const clearLocalStorage = () => {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(CURRENT_PROJECT_UUID_STORAGE_KEY);
  localStorage.removeItem(SAML_ORGANIZATION_UUID_STORAGE_KEY);
};

export const getTestIdWithPrefix = (id: string, prefix?: string | undefined) =>
  prefix ? `${prefix}-${id}` : id;

export const paginateData = <TEntity>(
  data: TEntity[],
  currentPage: number,
  rowsPerPage: number,
) => {
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  return data.slice(startIndex, endIndex);
};

export const formatNumberToK = (value: number): string => {
  if (value < 1000) {
    return Math.floor(value).toString();
  }
  const flooredK = Math.floor(value / 1000);
  return `${flooredK}k`;
};

export const formatError = (rawError: unknown): string => {
  if (typeof rawError === "string") {
    return rawError;
  }

  if (
    rawError &&
    typeof rawError === "object" &&
    "message" in rawError &&
    typeof (rawError as { message?: unknown }).message === "string"
  ) {
    return (rawError as { message: string }).message;
  }

  try {
    return JSON.stringify(rawError);
  } catch {
    return "Unknown error";
  }
};
